// ─────────────────────────────────────────────────────────────────
// V97.140 / V97.145 — Frais de laboratoire
// Date : 2026-07-15
//
// LES DEUX LABOS NE FACTURENT PAS DE LA MEME FACON :
//
//   Ortho-Analytic — 21.60 CHF PAR BON de commande envoye.
//
//   ⚠ CORRIGE LE 2026-08-22 : ce fichier disait « une fiche correspond a un
//   type de prelevement ». C'est faux, et ca se trompait dans les DEUX sens.
//   Un Mikroernaehrung (sang) + un Mikrobiom (selles) + une Haarmineral
//   (cheveux) tiennent tous les trois sur le seul bon allemand : 21.60, pas
//   64.80. A l'inverse, deux analyses sanguines dont une n'existe que sur le
//   bon francais demandent deux bons : 43.20, pas 21.60.
//
//   Ce qui compte est le nombre de BONS. Voir `sheetEstimator` plus bas et
//   `clinical/catalog/orthoBonsDeCommande.js`, qui sait sur quel bon tombe
//   chaque analyse.
//
//   MGD — 16.60 CHF de « taxe de commande » (position 4700.00) par COMMANDE.
//
//   ⚠ CORRIGE LE 2026-08-25, FACTURES EN MAIN. Ce fichier disait « une seule
//   fois par prescription, quel que soit le nombre d'analyses ET de
//   prelevements ». La premiere moitie est juste, la seconde est fausse.
//
//   Le releve du portail (1, puis 2, puis 5, puis 9 analyses cochees, forfait
//   immobile) ne testait qu'UNE commande, donc un seul prelevement. Le cycle
//   de Clelia en a produit DEUX : prise de sang le 20.07 (facture GEI0253959)
//   et salivaire le 21.07 (facture GEI0251282). Chacune porte sa propre taxe
//   de commande a 16.60. Prevu 16.60, facture 33.20.
//
//   Ce qui compte est le nombre de PRELEVEMENTS DISTINCTS : on ne prend pas
//   du sang et de la salive dans le meme geste, et un kit de selles part au
//   domicile puis revient a part. Plusieurs analyses sanguines tiennent en
//   revanche sur une seule commande, ce que le portail avait bien montre.
//
//   Le comble : le code calculait deja `estimatedSheets: 2` pour ce cycle,
//   puis la branche per_prescription l'ecrasait avec un 1 en dur. La bonne
//   reponse etait la, jetee a la derniere ligne.
//
//   MGD ajoute aussi 5.90 CHF de « prelevement de sang capillaire ou veineux »
//   (position 4701.00) sur toute commande sanguine. Voir SURCOUTS_PRELEVEMENT.
//
// D'ou `fee_model` sur lab_tests : il ne dit plus « une fois ou plusieurs »,
// il dit COMMENT on compte. Ortho compte des bons, MGD compte des commandes.
//
// On avait d'abord cru que MGD ne facturait rien (« MGD fait pas pareil »
// traduit par zero). C'est faux : MGD facture, autrement.
//
// Anissa avance les frais labo puis refacture la cliente : une taxe non
// comptee est une perte seche pour elle. Jusqu'ici `tax_chf` existait en base
// mais n'etait lue nulle part, donc jamais facturee.
//
// C'EST ANISSA QUI TRANCHE LE NOMBRE DE FICHES. Le systeme l'estime par
// nombre de types de prelevement distincts, mais cette estimation n'est
// qu'un defaut pre-rempli : elle seule remplit les fiches et sait combien il
// y en a vraiment. Deviner fermement serait faux (un prelevement peut tenir
// sur plusieurs fiches, ou l'inverse) et surtout invérifiable de notre cote.
// D'ou `sheetOverrides`, qui gagne toujours sur l'estimation.
// ─────────────────────────────────────────────────────────────────

export const UNKNOWN_SPECIMEN = 'inconnu';
export const UNKNOWN_LAB = 'inconnu';
export const PER_SHEET = 'per_sheet';
export const PER_PRESCRIPTION = 'per_prescription';

/**
 * Surcouts factures PAR COMMANDE selon le type de prelevement, en plus de la
 * taxe de commande.
 *
 * MGD facture 5.90 CHF le « prelevement de sang capillaire ou veineux »
 * (position 4701.00). Releve sur la facture GEI0253959 du 2026-08-17. Il
 * n'apparait QUE sur la commande sanguine : la commande salivaire du meme
 * cycle (GEI0251282) ne le porte pas, la cliente crache elle-meme.
 *
 * Ce montant vit ici et non dans `tax_chf` parce qu'il ne depend pas de
 * l'analyse mais du geste de prelevement : dix analyses sanguines sur une
 * meme prise ne le paient qu'une fois.
 *
 * ⚠ Un labo absent de cette table ne paie aucun surcout. Ortho facture ses
 * prelevements autrement (kits inclus dans le prix de l'analyse), d'ou son
 * absence — c'est un constat, pas un oubli.
 */
export const SURCOUTS_PRELEVEMENT = Object.freeze({
  mgd: Object.freeze({ sang: 5.9 }),
});

/** Surcout de prelevement d'un labo pour un jeu de types de prelevement. */
export function surcoutPrelevement(lab, specimens = []) {
  const bareme = SURCOUTS_PRELEVEMENT[lab];
  if (!bareme) return 0;
  return round2([...new Set(specimens)].reduce((somme, s) => somme + (bareme[s] || 0), 0));
}

/**
 * Regroupe des analyses par laboratoire et calcule ses frais.
 *
 * Deux modeles, selon `fee_model`. Les deux multiplient un tarif par un
 * compte et acceptent l'override d'Anissa ; ils different par ce qu'ils
 * comptent et par la facon de l'estimer :
 *  - `per_sheet` (Ortho) : compte des BONS de commande, estimes par
 *    `sheetEstimator` qui sait sur quel bon tombe chaque analyse.
 *  - `per_prescription` (MGD) : compte des COMMANDES, une par type de
 *    prelevement. Sang et salive ne se prelevent pas d'un meme geste.
 *
 * S'y ajoute le surcout de prelevement (`SURCOUTS_PRELEVEMENT`), qui suit les
 * types presents et non le compte de commandes.
 *
 * Defaut `per_sheet` si `fee_model` est absent : c'est le comportement
 * historique, et surtout celui qui ne SOUS-facture pas.
 *
 * Le tarif est le tax_chf le plus eleve du labo : au sein d'un meme labo le
 * montant est uniforme, et prendre le max evite de sous-facturer si une ligne
 * avait un tax_chf a 0 par erreur de saisie.
 *
 * 2026-08-22 — l'estimation par type de prelevement est fausse dans les deux
 * sens chez Ortho, ou ce qui compte est le nombre de BONS envoyes. Sang +
 * selles + cheveux tiennent sur le seul bon allemand : un bon, pas trois.
 * A l'inverse deux prises de sang dont une n'existe que sur le bon francais
 * en demandent deux. `sheetEstimator` laisse l'appelant fournir le vrai
 * compte ; ce fichier n'a pas a connaitre les bons d'un laboratoire.
 *
 * @param {Array<{code?: string, source_lab?: string, specimen_type?: string, tax_chf?: number|string, fee_model?: string}>} tests
 * @param {{
 *   sheetOverrides?: Record<string, number>,
 *   sheetEstimator?: (lab: string, codes: string[]) => number|null,
 * }} [opts]
 * @returns {{labs: Array<{lab: string, feeModel: string, taxUnit: number, estimatedSheets: number, sheetCount: number, isOverridden: boolean, tax: number, prelevement: number, testCount: number, specimens: string[]}>, totalTax: number, totalPrelevement: number, totalFrais: number, totalSheets: number, taxedSheets: number, hasUnknownSpecimen: boolean}}
 */
export function computeLabFees(tests = [], { sheetOverrides = {}, sheetEstimator = null } = {}) {
  // Meme forme que le retour nominal : une cle absente sur la branche vide
  // donnerait `undefined` au premier appelant qui la lirait.
  const empty = {
    labs: [], totalTax: 0, totalPrelevement: 0, totalFrais: 0,
    totalSheets: 0, taxedSheets: 0, hasUnknownSpecimen: false,
  };
  if (!Array.isArray(tests) || tests.length === 0) return empty;

  const map = new Map();
  for (const t of tests) {
    if (!t) continue;
    const lab = t.source_lab || UNKNOWN_LAB;
    const specimen = t.specimen_type || UNKNOWN_SPECIMEN;
    // tax_chf remonte de Supabase en string sur les colonnes numeric.
    const tax = Number(t.tax_chf) || 0;
    const feeModel = t.fee_model === PER_PRESCRIPTION ? PER_PRESCRIPTION : PER_SHEET;

    const prev = map.get(lab);
    if (prev) {
      prev.testCount += 1;
      if (t.code) prev.codes.push(String(t.code));
      prev.taxUnit = Math.max(prev.taxUnit, tax);
      prev.specimenSet.add(specimen);
      // Un labo panache (donnee incoherente) : on garde per_sheet, le modele
      // qui ne sous-facture pas.
      if (prev.feeModel !== feeModel) prev.feeModel = PER_SHEET;
    } else {
      map.set(lab, {
        lab, feeModel, taxUnit: tax, testCount: 1,
        specimenSet: new Set([specimen]),
        // Le code du PREMIER test doit entrer ici : l'oublier priverait
        // l'estimateur d'une analyse, donc potentiellement d'un bon entier.
        codes: t.code ? [String(t.code)] : [],
      });
    }
  }

  const labs = [...map.values()]
    .sort((a, b) => a.lab.localeCompare(b.lab))
    .map(({ lab, feeModel, taxUnit, testCount, specimenSet, codes }) => {
      const specimens = [...specimenSet].sort();

      // Les deux modeles comptent tous les deux, mais pas la meme chose.
      //
      // Ortho compte des BONS : l'appelant sait sur quel bon tombe chaque
      // analyse et le dit via `sheetEstimator`. Il rend null sur un test
      // inconnu, on retombe alors sur le nombre de prelevements, qui ne
      // sous-facture jamais.
      //
      // MGD compte des COMMANDES, et une commande vaut un prelevement. Le
      // catalogue des bons Ortho n'a rien a y dire, donc on ne lui demande
      // meme pas : ce serait lui faire arbitrer un labo qu'il ne connait pas.
      let estimatedSheets;
      if (feeModel === PER_PRESCRIPTION) {
        estimatedSheets = specimenSet.size;
      } else {
        const compte = typeof sheetEstimator === 'function' ? sheetEstimator(lab, codes) : null;
        estimatedSheets = Number.isFinite(compte) && compte >= 0 ? compte : specimenSet.size;
      }

      // Anissa tranche dans les deux cas. Elle seule remplit les bons et
      // signe les commandes ; elle seule sait si le labo a regroupe ou
      // scinde. L'estimation n'est qu'un defaut pre-rempli.
      const override = sheetOverrides[lab];
      const hasOverride = Number.isFinite(override) && override >= 0;
      const sheetCount = hasOverride ? Math.floor(override) : estimatedSheets;

      // Le surcout de prelevement suit les TYPES presents, pas le compte de
      // commandes : si Anissa corrige « 2 commandes » en « 1 », le sang reste
      // du sang et se preleve toujours une fois.
      const prelevement = surcoutPrelevement(lab, specimens);

      return {
        lab,
        feeModel,
        taxUnit,
        estimatedSheets,
        sheetCount,
        isOverridden: hasOverride && sheetCount !== estimatedSheets,
        tax: round2(sheetCount * taxUnit),
        prelevement,
        testCount,
        specimens,
      };
    });

  const totalTax = round2(labs.reduce((sum, l) => sum + l.tax, 0));
  const totalPrelevement = round2(labs.reduce((sum, l) => sum + l.prelevement, 0));

  return {
    labs,
    totalTax,
    totalPrelevement,
    // ⚠ C'EST CE MONTANT QU'IL FAUT FACTURER, pas `totalTax`. Ce dernier
    // ignore les surcouts de prelevement, et ce qu'on oublie de compter ici,
    // Anissa le paie de sa poche : elle avance les frais puis refacture.
    totalFrais: round2(totalTax + totalPrelevement),
    totalSheets: labs.reduce((sum, l) => sum + l.sheetCount, 0),
    // FICHES facturees, au sens propre. Une commande MGD n'est pas une fiche :
    // la compter ici ferait ecrire « 3 fiches » a une ligne qui n'en a qu'une.
    // Seuls les labos per_sheet comptent.
    taxedSheets: labs.reduce(
      (sum, l) => sum + (l.taxUnit > 0 && l.feeModel !== PER_PRESCRIPTION ? l.sheetCount : 0),
      0
    ),
    hasUnknownSpecimen: labs.some(l => l.specimens.includes(UNKNOWN_SPECIMEN)),
  };
}

// Neutralise le bruit flottant (21.6 * 2 = 43.199999...).
function round2(n) {
  return Math.round(n * 100) / 100;
}
